import { z } from 'zod';

/**
 * Primitives shared by every request and response shape. Defining them once means a form
 * on the client and a handler on the server can never disagree about what a valid mobile
 * number or a valid page size is.
 */

/**
 * Identity in this product is the email address. It is the username, the address an OTP is
 * sent to, and the only thing a person types to say who they are.
 *
 * It NORMALISES as well as validates — trimmed and lower-cased — because `Owner@Shop.com` and
 * `owner@shop.com` are the same mailbox, and letting them resolve to two accounts is how one
 * person ends up unable to reach the shop they created.
 *
 * The length cap is the practical limit for the local part plus domain in RFC 5321; the point
 * is to refuse a megabyte of text at the schema rather than in the mail provider.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Enter your email address')
  .max(254, 'That email address is too long')
  .pipe(z.string().email('Enter a valid email address'));

/**
 * A customer's, supplier's or vendor's mobile number: 10 digits, Indian series (6-9).
 *
 * This is CONTACT data — it is what WhatsApp order updates are sent to — and is never a login
 * credential; see {@link emailSchema} for identity.
 *
 * Two prefixes are stripped, and each ONLY when what remains is a full 10-digit number:
 *
 *  - the `0` trunk prefix, which is how a shop still writes a number it is dictating over the
 *    phone (`098765 43210`);
 *  - the `+91` country code.
 *
 * The lookaheads are doing real work: without them, `9123456789` — a perfectly ordinary
 * Vodafone number — has its own first two digits eaten as a country code and becomes an
 * 8-digit string that fails validation, so nobody whose number starts with 91 could sign up
 * or sign in at all.
 */
export const indianMobileSchema = z
  .string()
  .trim()
  .transform((value) =>
    value
      .replace(/[\s-]/g, '')
      .replace(/^0(?=\d{10}$)/, '')
      .replace(/^\+?91(?=\d{10}$)/, ''),
  )
  .pipe(z.string().regex(/^[6-9]\d{9}$/, 'Enter a 10-digit Indian mobile number'));

/**
 * The two shapes an entity id takes while the data moves from MongoDB to Postgres.
 *
 * Rows that already exist keep their 24-hex ObjectId string for life; rows created after the
 * move get a UUID (v7 — see `newEntityId()`). Both are ordinary ids to every caller, so the
 * contract accepts either and nothing downstream may assume one length.
 *
 * The UUID form is the canonical 8-4-4-4-12 hex layout, case-insensitive. The version and
 * variant nibbles are deliberately NOT checked: an id is an opaque key here, and an importer
 * that mints a v4 must not be refused at the door.
 */
const ENTITY_ID_PATTERN =
  /^(?:[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** Whether `value` is a well-formed entity id: a 24-hex ObjectId string or a canonical UUID. */
export function isEntityId(value: unknown): value is string {
  return typeof value === 'string' && ENTITY_ID_PATTERN.test(value);
}

/**
 * An entity id in string form, as it crosses the wire: a legacy 24-hex ObjectId or a UUID.
 * The name is historical and kept so the dozens of schemas built on it did not have to move;
 * new code should prefer {@link entityIdSchema}, which is the same schema.
 *
 * Still a plain `ZodString` (a regex, not a refinement) so every `.optional()`, `.nullish()`
 * and string method chained off it elsewhere keeps its type.
 */
export const objectIdSchema = z.string().regex(ENTITY_ID_PATTERN, 'Invalid id');

/** The same schema as {@link objectIdSchema}, under the name that says what it now accepts. */
export const entityIdSchema = objectIdSchema;

/** Every document in this system carries the id of the shop that owns it. */
export const tenantIdSchema = objectIdSchema;

/** A 4-digit PIN — how tailors sign in. */
export const pinSchema = z.string().regex(/^\d{4}$/, 'PIN must be exactly 4 digits');

export const passwordSchema = z.string().min(8, 'Password must be at least 8 characters').max(128);

/** Indian state code used for place-of-supply decisions on a GST invoice. */
export const stateCodeSchema = z.string().regex(/^\d{2}$/, 'State code must be 2 digits');

export const isoDateStringSchema = z.string().datetime({ offset: true });

/**
 * A date as it actually crosses the wire.
 *
 * A form sends `2026-08-20` from `<input type="date">` and server code passes a `Date`, so the
 * schema accepts both and normalises to a `Date`. `z.coerce.date()` would do the same at
 * runtime but types its INPUT as `Date`, which makes every browser caller a type error — the
 * union is what keeps the shared type honest about what a client may send.
 */
export const dateInputSchema = z
  .union([z.date(), z.string().trim().min(1)])
  .transform((value) => (value instanceof Date ? value : new Date(value)))
  .refine((date) => !Number.isNaN(date.getTime()), 'Enter a valid date');

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

/**
 * The envelope every rejected request uses. `code` is machine-readable so the client can
 * render a specific message (a plan limit becomes a contextual upgrade prompt rather than
 * a generic "upgrade required"), and `fieldErrors` maps straight onto form inputs.
 */
export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  fieldErrors: z.record(z.array(z.string())).optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const API_ERROR_CODES = {
  VALIDATION_FAILED: 'validation_failed',
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict',
  PLAN_LIMIT_REACHED: 'plan_limit_reached',
  MODULE_NOT_IN_PLAN: 'module_not_in_plan',
  SUBSCRIPTION_READ_ONLY: 'subscription_read_only',
  RATE_LIMITED: 'rate_limited',
  /** `MAINTENANCE=1`: every route but `/health` is closed for a cutover or a data repair. */
  MAINTENANCE: 'maintenance',
  INTERNAL_ERROR: 'internal_error',
} as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];
